// ProjectAddMenu — global "+ Neu" dropdown in the project header.
//
// Lets the user add an Aufgabe, Phase or Meilenstein from ANY view (Tabelle,
// Zeitleiste, Kanban, Kalender, Meilensteine, Auswertung). Previously task
// creation only lived inside the table/kanban views and phase creation only at
// the bottom of the table — so in the timeline/calendar/analytics views there
// was no way to add anything. This menu closes that gap.
//
// Tasks and milestones require a phase (tasks are phase-scoped). When the
// project has no phases yet, the task/milestone dialogs guide the user to
// create a phase first.

import { useEffect, useRef, useState } from "preact/hooks";
import { ChevronDown, Flag, Layers, ListTodo, Plus, Sparkles } from "lucide-preact";
import type { ProjectWithCounts, TaskCreate, TaskPriority } from "@juuplan/shared";
import { TASK_PRIORITIES } from "@juuplan/shared";
import { createPhase, createTask } from "../store/project";
import { TASK_PRIORITY_LABEL } from "../lib/format";
import { toastError, toastSuccess } from "../lib/toast";
import { openTaskImport } from "../store/taskImport";
import { useMe } from "../lib/auth";
import { Modal } from "./Modal";

import { Button } from "./ui/Button";

type DialogKind = "task" | "phase" | "milestone";
/** „import" öffnet den globalen KI-Listenimport (#1297), keinen eigenen Dialog. */
type MenuKind = DialogKind | "import";

interface MenuItem {
  kind: MenuKind;
  icon: preact.JSX.Element;
  label: string;
}

const INPUT_CLASS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 transition-colors disabled:opacity-60";

const LABEL_CLASS =
  "block text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-1";

interface Props {
  project: ProjectWithCounts;
}

export function ProjectAddMenu({ project }: Props): preact.JSX.Element {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const me = useMe();
  const aiOn = me.user?.ai_enabled === true && !me.user.is_external;

  const items: MenuItem[] = [
    { kind: "task", icon: <ListTodo size={16} aria-hidden="true" />, label: "Aufgabe" },
    { kind: "phase", icon: <Layers size={16} aria-hidden="true" />, label: "Phase" },
    { kind: "milestone", icon: <Flag size={16} aria-hidden="true" />, label: "Meilenstein" },
    ...(aiOn
      ? [{ kind: "import" as const, icon: <Sparkles size={16} aria-hidden="true" />, label: "Liste importieren (KI)" }]
      : []),
  ];

  // Click-outside closes the dropdown (mirrors ViewPicker).
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pick = (kind: MenuKind): void => {
    setOpen(false);
    if (kind === "import") {
      openTaskImport({ target: "project", projectId: project.id });
      return;
    }
    setDialog(kind);
  };

  return (
    <div ref={rootRef} class="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        class="inline-flex items-center gap-1.5 rounded border border-transparent bg-[color:var(--color-primary)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-primary)] hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
        data-testid="project-add-menu-trigger"
      >
        <Plus size={14} aria-hidden="true" />
        Neu
        <ChevronDown
          size={14}
          aria-hidden="true"
          class={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          class="absolute right-0 top-full z-40 mt-1 min-w-[180px] rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-3 py-1"
          role="menu"
        >
          {items.map((it) => (
            <button
              key={it.kind}
              type="button"
              role="menuitem"
              onClick={() => pick(it.kind)}
              class="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus:bg-[color:var(--color-surface-container-low)] transition-colors"
              data-testid={`project-add-${it.kind}`}
            >
              <span class="text-[color:var(--color-on-surface-variant)]">{it.icon}</span>
              {it.label}
            </button>
          ))}
        </div>
      )}

      {dialog === "phase" && (
        <PhaseDialog project={project} onClose={() => setDialog(null)} />
      )}
      {dialog === "task" && (
        <TaskDialog
          project={project}
          milestone={false}
          onClose={() => setDialog(null)}
          onNeedPhase={() => setDialog("phase")}
        />
      )}
      {dialog === "milestone" && (
        <TaskDialog
          project={project}
          milestone
          onClose={() => setDialog(null)}
          onNeedPhase={() => setDialog("phase")}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase dialog
// ---------------------------------------------------------------------------

function PhaseDialog({
  project,
  onClose,
}: {
  project: ProjectWithCounts;
  onClose: () => void;
}): preact.JSX.Element {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await createPhase(project.id, { name: trimmed });
      toastSuccess("Phase angelegt.");
      onClose();
    } catch {
      toastError("Phase konnte nicht angelegt werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Neue Phase"
      onClose={onClose}
      size="sm"
      dirty={name.trim().length > 0}
      footer={
        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
          >
            Abbrechen
          </button>
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={() => void submit()}
            disabled={busy || !name.trim()}
          >
            {busy ? "Anlegen …" : "Phase anlegen"}
          </Button>
        </div>
      }
    >
      <label class="block">
        <span class={LABEL_CLASS}>Name der Phase</span>
        <input
          type="text"
          value={name}
          autoFocus
          disabled={busy}
          maxLength={120}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder="z. B. Konzeption"
          aria-label="Name der Phase"
          class={INPUT_CLASS}
        />
      </label>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Task / Milestone dialog (shared — milestone preselects is_milestone + a date)
// ---------------------------------------------------------------------------

function TaskDialog({
  project,
  milestone,
  onClose,
  onNeedPhase,
}: {
  project: ProjectWithCounts;
  milestone: boolean;
  onClose: () => void;
  onNeedPhase: () => void;
}): preact.JSX.Element {
  const phases = project.phases;
  const [phaseId, setPhaseId] = useState<number | null>(phases[0]?.id ?? null);
  const [title, setTitle] = useState("");
  const [dateValue, setDateValue] = useState(""); // due date (task) / target date (milestone)
  const [priority, setPriority] = useState<TaskPriority | "">("");
  const [busy, setBusy] = useState(false);

  const noPhases = phases.length === 0;
  const kindLabel = milestone ? "Meilenstein" : "Aufgabe";

  const submit = async (): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed || busy || phaseId === null) return;
    setBusy(true);
    try {
      const body: TaskCreate = { title: trimmed };
      if (milestone) {
        body.is_milestone = true;
        // A milestone is a point in time — store the chosen date as its deadline.
        if (dateValue) body.deadline = dateValue;
      } else {
        if (dateValue) body.end_date = dateValue;
        if (priority) body.priority = priority;
      }
      await createTask(phaseId, body);
      toastSuccess(`${kindLabel} angelegt.`);
      onClose();
    } catch {
      toastError(`${kindLabel} konnte nicht angelegt werden.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={milestone ? "Neuer Meilenstein" : "Neue Aufgabe"}
      onClose={onClose}
      size="sm"
      dirty={title.trim().length > 0}
      footer={
        !noPhases && (
          <div class="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded border border-[color:var(--color-outline-variant)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
            >
              Abbrechen
            </button>
            <Button
              variant="primary"
              size="lg"
              type="button"
              onClick={() => void submit()}
              disabled={busy || !title.trim() || phaseId === null}
            >
              {busy ? "Anlegen …" : `${kindLabel} anlegen`}
            </Button>
          </div>
        )
      }
    >
      {noPhases ? (
        <div class="flex flex-col items-start gap-3 py-2">
          <p class="text-sm text-[color:var(--color-on-surface)]">
            Dieses Projekt hat noch keine Phase. {kindLabel === "Aufgabe" ? "Aufgaben" : "Meilensteine"} gehören immer zu
            einer Phase — lege zuerst eine an.
          </p>
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={onNeedPhase}
          >
            <Layers size={14} aria-hidden="true" />
            Phase anlegen
          </Button>
        </div>
      ) : (
        <div class="flex flex-col gap-4">
          <label class="block">
            <span class={LABEL_CLASS}>Titel</span>
            <input
              type="text"
              value={title}
              autoFocus
              disabled={busy}
              maxLength={200}
              onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder={milestone ? "z. B. Go-Live" : "z. B. Angebot erstellen"}
              aria-label="Titel"
              class={INPUT_CLASS}
            />
          </label>

          <label class="block">
            <span class={LABEL_CLASS}>Phase</span>
            <select
              value={phaseId === null ? "" : String(phaseId)}
              disabled={busy}
              onChange={(e) =>
                setPhaseId(Number((e.target as HTMLSelectElement).value))
              }
              aria-label="Phase"
              class={INPUT_CLASS}
            >
              {phases.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>

          <label class="block">
            <span class={LABEL_CLASS}>{milestone ? "Termin" : "Fällig am"} (optional)</span>
            <input
              type="date"
              value={dateValue}
              disabled={busy}
              onInput={(e) => setDateValue((e.target as HTMLInputElement).value)}
              aria-label={milestone ? "Termin" : "Fällig am"}
              class={INPUT_CLASS}
            />
          </label>

          {!milestone && (
            <label class="block">
              <span class={LABEL_CLASS}>Priorität (optional)</span>
              <select
                value={priority}
                disabled={busy}
                onChange={(e) =>
                  setPriority((e.target as HTMLSelectElement).value as TaskPriority | "")
                }
                aria-label="Priorität"
                class={INPUT_CLASS}
              >
                <option value="">— Keine —</option>
                {(TASK_PRIORITIES as readonly TaskPriority[]).map((p) => (
                  <option key={p} value={p}>
                    {TASK_PRIORITY_LABEL[p]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </Modal>
  );
}
